import { Alert, Button, Form, Modal } from "antd";
import { useIdentity } from "context/auth";
import useAccountSummary from "hooks/useAccountSummary";
import { useState } from "react";
import { centsToDollars, dollarsToCents } from "util/helpers";
import AmountFormItem from "../Payouts/RequestPayoutModal/PayoutMethodDetailForms/Inputs/AmountFormItem";
import { AlertState, TransferState } from "./TransferState";
import useFundsTransfer, { BalanceTransferType } from "./useFundsTransfer";

interface BreakerFundsTransferModalProps {
    breakerId: string;
    breakerUserName: string;
    breakerCreditBalanceInCents: number;
    onFundsTransfer: () => Promise<void>
}

const BreakerFundsTransferModal = ({ breakerId, breakerUserName, breakerCreditBalanceInCents, onFundsTransfer }: BreakerFundsTransferModalProps) => {
    const { accounts } = useIdentity();
    const { isLoading, data = { availableBalanceInCents: NaN }, refetch } = useAccountSummary(accounts[0]);
    const [open, setOpen] = useState(false);
    const [preFlight, setPreFlight] = useState<{ amountInCents: number }>();
    const [state, setState] = useState(() => TransferState.Start);
    const [form] = Form.useForm<{ amount: number }>();
    const { transferFunds, isWorking } = useFundsTransfer(BalanceTransferType.ConsignmentToSilent);

    const { availableBalanceInCents } = data;

    const noFunds = availableBalanceInCents === 0 && state === TransferState.Start;

    const reset = () => {
        setOpen(false);
        setPreFlight(undefined);
        setState(TransferState.Start);
        form.resetFields();
    }

    const success = async () => {
        await refetch();
        await onFundsTransfer();
        setState(TransferState.Success);
    }

    const submit = async () => {
        setState(TransferState.Submitting);

        try {
            await transferFunds({
                amountInCents: preFlight!.amountInCents,
                breakerId
            });

            await success();
        } catch {
            setState(TransferState.Error);
        }
    }

    const nextStep = async () => {
        switch (state) {
            case TransferState.Start: {
                const value = await form.validateFields();
                const amountInCents = dollarsToCents(value.amount.toString());

                setPreFlight({ amountInCents });
                setState(TransferState.Validated);
                break;
            }
            case TransferState.Validated: {
                await submit();
                break;
            }
            case TransferState.Success: {
                reset();
            }
        }
    }

    const okText = state === TransferState.Success
        ? "OK"
        : state === TransferState.Validated
            ? "Transfer Funds"
            : "Next";

    return <>
        <Button
            onClick={() => setOpen(true)}
            shape="circle"
            size="large"
            style={{ backgroundColor: 'var(--dcs-dark-green)', color: 'var(--dcs-white)' }}>
            +$
        </Button>
        <Modal
            title={`Transfer Funds to ${breakerUserName}`}
            open={open}
            onOk={nextStep}
            onCancel={reset}
            destroyOnClose
            maskClosable={false}
            keyboard={false}
            closable={false}
            confirmLoading={isLoading || isWorking}
            okButtonProps={{ hidden: noFunds }}
            cancelButtonProps={{ disabled: isLoading || isWorking, hidden: state === TransferState.Success }}
            okText={okText}
        >
            <Form form={form} preserve={false} disabled={!!preFlight || noFunds}>
                <div style={{ fontSize: '26px' }}>Available Balance: {centsToDollars(availableBalanceInCents)}</div>
                <p>Transfer funds from your available balance to your credit balance with {breakerUserName}, which is currently {centsToDollars(breakerCreditBalanceInCents)}.</p>

                <div style={{ marginTop: '20px' }}>
                    <AmountFormItem max={availableBalanceInCents} />

                    {noFunds && <Alert type="error" message="No funds available to transfer!" showIcon />}
                </div>
            </Form>

            <AlertState
                state={state}
                validatedMessage={`You will be transfering ${centsToDollars(preFlight?.amountInCents ?? 0)} to your credit balance with ${breakerUserName}`}
            />
        </Modal>
    </>
};

export default BreakerFundsTransferModal;